import React, { useRef, useState, useEffect } from 'react';
import { Point, Line, TextNote, BackgroundType, ToolType, DrawingState, UIState, LineStyle } from './types';
import { drawBackground } from './Background';

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const DEFAULT_SCALE = 1;
const DEFAULT_OFFSET_X = 0;
const DEFAULT_OFFSET_Y = 0;
const DEFAULT_LINE_WIDTH = 3;
const DEFAULT_COLOR = '#000000';
const DEFAULT_ERASER_SIZE = 30;
const DOUBLE_CLICK_TIME = 300;
const ERASER_CLICK_RADIUS = 20;
const SELECTION_THRESHOLD = 5;
const RESIZE_HANDLE_SIZE = 10;
const LINE_ENDPOINT_SIZE = 8;
const BASE_CANVAS_WIDTH = 2500;
const EXTRA_SCROLL_WIDTH = 1000;
const MAX_CANVAS_WIDTH = BASE_CANVAS_WIDTH + EXTRA_SCROLL_WIDTH;
const MIN_ERASER_RADIUS = 5;
const MAX_ERASER_RADIUS = 100;
const MIN_LINE_LENGTH = 5;
const MIN_SELECTION_SIZE = 5;

export const CanvasComponent = ({
  backgroundType,
  drawingState,
  uiState,
  onUiStateChange,
  onAddLine,
  onDeleteLine,
  onUpdateLines
}: {
  backgroundType: BackgroundType;
  drawingState: DrawingState;
  uiState: UIState;
  onUiStateChange: (state: UIState) => void;
  onAddLine: (line: Line) => void;
  onDeleteLine: (lineId: string) => void;
  onUpdateLines: (lines: Line[], textNotes: TextNote[]) => void;
}) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const [isErasing, setIsErasing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [currentLine, setCurrentLine] = useState<Line | null>(null);
  const [lastClickTime, setLastClickTime] = useState(0);
  const [lastClickPosition, setLastClickPosition] = useState<Point | null>(null);
  const [lastMousePosition, setLastMousePosition] = useState<Point | null>(null);
  const [eraserStartPosition, setEraserStartPosition] = useState<Point | null>(null);
  const [tempHandMode, setTempHandMode] = useState(false);
  const [previousTool, setPreviousTool] = useState<ToolType | null>(null);
  const [lastEraseTime, setLastEraseTime] = useState(0);
  const [lastErasedLineIds, setLastErasedLineIds] = useState<string[]>([]);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | null>(null);
  const [resizeStart, setResizeStart] = useState<{
    box: { x: number, y: number, width: number, height: number },
    mouse: Point
  } | null>(null);
  const [prevDragPosition, setPrevDragPosition] = useState<Point | null>(null);
  const [prevResizePosition, setPrevResizePosition] = useState<Point | null>(null);
  const [lineEndpointHandle, setLineEndpointHandle] = useState<'start' | 'end' | null>(null);
  const [lineEndpointStart, setLineEndpointStart] = useState<{
    line: Line,
    mouse: Point
  } | null>(null);
  const [activeLineForEndpoint, setActiveLineForEndpoint] = useState<Line | null>(null);
  const [isEditingText, setIsEditingText] = useState(false);
  const [editingTextNote, setEditingTextNote] = useState<TextNote | null>(null);
  const [textInputValue, setTextInputValue] = useState('');
  const [textInputHeight, setTextInputHeight] = useState(50);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const dpr = window.devicePixelRatio || 1;

  const calculateTextHeight = (text: string, fontSize: number, width: number): number => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;
    ctx.font = `${fontSize}px Arial`;

    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine + (currentLine ? ' ' : '') + word;
      const metrics = ctx.measureText(testLine);

      if (metrics.width > width - 10 && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    lines.push(currentLine);

    return Math.max(50, lines.length * fontSize * 1.2 + 10);
  };

  const getAdaptiveDashPattern = (style: LineStyle, width: number, scale: number): number[] => {
    const baseSize = Math.max(1.5, width * scale * 0.8);
    
    switch (style) {
      case 'dashed':
        return [baseSize * 4, baseSize * 2];
      case 'dotted':
        return [baseSize * 0.5, baseSize * 2.5];
      default:
        return [];
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const updateCanvasSize = () => {
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;

      canvas.width = width * dpr;
      canvas.height = height * dpr;

      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      redraw();
    };

    updateCanvasSize();

    const resizeObserver = new ResizeObserver(updateCanvasSize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, []);

  // ИСПРАВЛЕНИЕ: Убрал uiState из зависимостей
  useEffect(() => {
    redraw();
  }, [
    drawingState.lines,
    drawingState.textNotes,
    backgroundType,
    uiState.offsetX,
    uiState.offsetY,
    uiState.scale,
    currentLine,
    isEditingText,
    textInputHeight,
    uiState.selection
  ]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftPressed(true);
      if (e.key === 'Escape' && isEditingText) {
        setIsEditingText(false);
        setEditingTextNote(null);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') setIsShiftPressed(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isEditingText]);

  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { offsetX, offsetY, scale } = uiState;
    const container = containerRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    ctx.clearRect(0, 0, width * dpr, height * dpr);
    ctx.scale(dpr, dpr);

    drawBackground(ctx, width, height, backgroundType, offsetX, offsetY, scale);

    drawingState.lines.forEach(line => {
      drawLine(ctx, line, offsetX, offsetY, scale, uiState.selection.includes(line.id));
    });

    drawingState.textNotes?.forEach(note => {
      drawTextNote(ctx, note, offsetX, offsetY, scale, uiState.selection.includes(note.id));
    });

    if (currentLine && (uiState.tool === 'pen' || uiState.tool === 'line') && !tempHandMode) {
      drawLine(ctx, currentLine, offsetX, offsetY, scale, false);
    }

    if (uiState.tool === 'pointer' && uiState.isSelecting && uiState.selectionBox) {
      drawSelectionBox(ctx, uiState.selectionBox, offsetX, offsetY, scale);
    }

    if (uiState.tool === 'pointer' && uiState.selection.length > 0) {
      const selectionBox = getSelectionBoundingBox(drawingState, uiState.selection);
      if (selectionBox) {
        drawResizeHandles(ctx, selectionBox, offsetX, offsetY, scale);
      }
    }

    ctx.setTransform(1, 0, 0, 1, 0, 0);
  };

  const drawLine = (
    ctx: CanvasRenderingContext2D,
    line: Line,
    offsetX: number,
    offsetY: number,
    scale: number,
    isSelected: boolean
  ) => {
    if (line.points.length < 2) return;

    ctx.beginPath();
    const dashPattern = getAdaptiveDashPattern(line.lineStyle, line.width, scale);
    if (dashPattern.length > 0) {
      ctx.setLineDash(dashPattern);
    } else {
      ctx.setLineDash([]);
    }

    const firstPoint = transformPoint(line.points[0], offsetX, offsetY, scale);
    ctx.moveTo(firstPoint.x, firstPoint.y);

    for (let i = 1; i < line.points.length; i++) {
      const point = transformPoint(line.points[i], offsetX, offsetY, scale);
      ctx.lineTo(point.x, point.y);
    }

    if (isSelected) {
      ctx.lineWidth = line.width + 2;
      ctx.strokeStyle = '#3498db';
      ctx.stroke();
      
      ctx.lineWidth = line.width;
      ctx.strokeStyle = line.color;
    } else {
      ctx.strokeStyle = line.color;
      ctx.lineWidth = line.width;
    }

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.setLineDash([]);
  };

  const drawTextNote = (
    ctx: CanvasRenderingContext2D,
    note: TextNote,
    offsetX: number,
    offsetY: number,
    scale: number,
    isSelected: boolean
  ) => {
    const screenX = note.x * scale - offsetX;
    const screenY = note.y * scale - offsetY;
    const screenWidth = note.width * scale;
    const screenHeight = note.height * scale;

    ctx.font = `${note.fontSize * scale}px Arial`;
    ctx.fillStyle = note.color;
    ctx.textBaseline = 'top';

    const words = note.text.split(' ');
    const lines = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine + (currentLine ? ' ' : '') + word;
      const metrics = ctx.measureText(testLine);
      const testWidth = metrics.width;

      if (testWidth > screenWidth - 10 * scale && currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        currentLine = testLine;
      }
    }
    lines.push(currentLine);

    lines.forEach((line, index) => {
      ctx.fillText(line, screenX + 5 * scale, screenY + 5 * scale + index * note.fontSize * scale * 1.2);
    });

    if (isSelected) {
      ctx.strokeStyle = '#3498db';
      ctx.lineWidth = 2;
      ctx.setLineDash([]);
      ctx.strokeRect(screenX, screenY, screenWidth, screenHeight);
    }
  };

  const drawSelectionBox = (
    ctx: CanvasRenderingContext2D,
    selectionBox: { x: number; y: number; width: number; height: number },
    offsetX: number,
    offsetY: number,
    scale: number
  ) => {
    ctx.strokeStyle = '#3498db';
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);

    const screenX = selectionBox.x * scale - offsetX;
    const screenY = selectionBox.y * scale - offsetY;

    ctx.beginPath();
    ctx.rect(screenX, screenY, selectionBox.width * scale, selectionBox.height * scale);
    ctx.stroke();

    ctx.setLineDash([]);
  };

  const drawResizeHandles = (
    ctx: CanvasRenderingContext2D,
    selectionBox: { x: number; y: number; width: number; height: number },
    offsetX: number,
    offsetY: number,
    scale: number
  ) => {
    const screenX = selectionBox.x * scale - offsetX;
    const screenY = selectionBox.y * scale - offsetY;
    const screenWidth = selectionBox.width * scale;
    const screenHeight = selectionBox.height * scale;

    const handles = [
      { x: screenX, y: screenY, position: 'nw' as const },
      { x: screenX + screenWidth / 2, y: screenY, position: 'n' as const },
      { x: screenX + screenWidth, y: screenY, position: 'ne' as const },
      { x: screenX + screenWidth, y: screenY + screenHeight / 2, position: 'e' as const },
      { x: screenX + screenWidth, y: screenY + screenHeight, position: 'se' as const },
      { x: screenX + screenWidth / 2, y: screenY + screenHeight, position: 's' as const },
      { x: screenX, y: screenY + screenHeight, position: 'sw' as const },
      { x: screenX, y: screenY + screenHeight / 2, position: 'w' as const }
    ];

    ctx.fillStyle = '#3498db';
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1;

    handles.forEach(handle => {
      ctx.beginPath();
      ctx.rect(handle.x - RESIZE_HANDLE_SIZE / 2, handle.y - RESIZE_HANDLE_SIZE / 2,
               RESIZE_HANDLE_SIZE, RESIZE_HANDLE_SIZE);
      ctx.fill();
      ctx.stroke();
    });
  };

  const transformPoint = (point: Point, offsetX: number, offsetY: number, scale: number): Point => {
    return {
      x: point.x * scale - offsetX,
      y: point.y * scale - offsetY
    };
  };

  const inverseTransformPoint = (point: Point, offsetX: number, offsetY: number, scale: number): Point => {
    return {
      x: (point.x + offsetX) / scale,
      y: (point.y + offsetY) / scale
    };
  };

  const getPointerPosition = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    
    if ('touches' in e) {
      const touch = e.touches[0] || e.changedTouches[0];
      return {
        x: touch.clientX - rect.left,
        y: touch.clientY - rect.top
      };
    } else {
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
    }
  };

  const getSelectionBoundingBox = (drawingState: DrawingState, selection: string[]): any => {
    if (selection.length === 0) return null;

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const id of selection) {
      const line = drawingState.lines.find(l => l.id === id);
      if (line) {
        for (const point of line.points) {
          minX = Math.min(minX, point.x);
          minY = Math.min(minY, point.y);
          maxX = Math.max(maxX, point.x);
          maxY = Math.max(maxY, point.y);
        }
      }

      const note = drawingState.textNotes?.find(n => n.id === id);
      if (note) {
        minX = Math.min(minX, note.x);
        minY = Math.min(minY, note.y);
        maxX = Math.max(maxX, note.x + note.width);
        maxY = Math.max(maxY, note.y + note.height);
      }
    }

    if (minX === Infinity) return null;

    const padding = 5 / uiState.scale;
    return {
      x: minX - padding,
      y: minY - padding,
      width: (maxX - minX) + padding * 2,
      height: (maxY - minY) + padding * 2
    };
  };

  const isPointInResizeHandle = (
    point: Point,
    selectionBox: { x: number, y: number, width: number, height: number },
    offsetX: number,
    offsetY: number,
    scale: number
  ): 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | null => {
    const screenX = selectionBox.x * scale - offsetX;
    const screenY = selectionBox.y * scale - offsetY;
    const screenWidth = selectionBox.width * scale;
    const screenHeight = selectionBox.height * scale;

    const handles = [
      { x: screenX, y: screenY, position: 'nw' as const },
      { x: screenX + screenWidth / 2, y: screenY, position: 'n' as const },
      { x: screenX + screenWidth, y: screenY, position: 'ne' as const },
      { x: screenX + screenWidth, y: screenY + screenHeight / 2, position: 'e' as const },
      { x: screenX + screenWidth, y: screenY + screenHeight, position: 'se' as const },
      { x: screenX + screenWidth / 2, y: screenY + screenHeight, position: 's' as const },
      { x: screenX, y: screenY + screenHeight, position: 'sw' as const },
      { x: screenX, y: screenY + screenHeight / 2, position: 'w' as const }
    ];

    for (const handle of handles) {
      const handleLeft = handle.x - RESIZE_HANDLE_SIZE / 2;
      const handleRight = handle.x + RESIZE_HANDLE_SIZE / 2;
      const handleTop = handle.y - RESIZE_HANDLE_SIZE / 2;
      const handleBottom = handle.y + RESIZE_HANDLE_SIZE / 2;

      if (point.x >= handleLeft && point.x <= handleRight &&
          point.y >= handleTop && point.y <= handleBottom) {
        return handle.position;
      }
    }

    return null;
  };

  const isPointInLineEndpoint = (
    point: Point,
    line: Line,
    offsetX: number,
    offsetY: number,
    scale: number
  ): 'start' | 'end' | null => {
    if (line.points.length !== 2) return null;

    const startPoint = transformPoint(line.points[0], offsetX, offsetY, scale);
    const endPoint = transformPoint(line.points[1], offsetX, offsetY, scale);

    const startDistance = Math.sqrt(
      Math.pow(point.x - startPoint.x, 2) +
      Math.pow(point.y - startPoint.y, 2)
    );

    const endDistance = Math.sqrt(
      Math.pow(point.x - endPoint.x, 2) +
      Math.pow(point.y - endPoint.y, 2)
    );

    const threshold = LINE_ENDPOINT_SIZE + 3;

    if (startDistance <= threshold) {
      return 'start';
    } else if (endDistance <= threshold) {
      return 'end';
    }

    return null;
  };

  const startHandMode = () => {
    if (!tempHandMode) {
      setPreviousTool(uiState.tool);
      setTempHandMode(true);
    }
  };

  const endHandMode = () => {
    if (tempHandMode && previousTool) {
      setTempHandMode(false);
      setPreviousTool(null);
    }
  };

  const clampOffsetX = (offsetX: number, scale: number, width: number): number => {
    const minOffsetX = 0;
    const maxOffsetX = Math.max(0, MAX_CANVAS_WIDTH * scale - width);
    return Math.max(minOffsetX, Math.min(maxOffsetX, offsetX));
  };

  const clampOffsetY = (offsetY: number, scale: number, height: number): number => {
  const minOffsetY = 0; // Ограничение сверху
  const maxOffsetY = Number.MAX_SAFE_INTEGER;
  return Math.max(minOffsetY, Math.min(maxOffsetY, offsetY));
  };

  const findTextNoteAt = (point: Point): TextNote | null => {
    const notes = drawingState.textNotes || [];
    return notes.find(note =>
      point.x >= note.x &&
      point.x <= note.x + note.width &&
      point.y >= note.y &&
      point.y <= note.y + note.height
    ) || null;
  };

  const findClosestLine = (point: Point, threshold: number, scale: number): Line | null => {
    const virtualThreshold = threshold / scale;

    let closestLine: Line | null = null;
    let minDistance = Infinity;

    for (const line of drawingState.lines) {
      for (let i = 0; i < line.points.length - 1; i++) {
        const p1 = line.points[i];
        const p2 = line.points[i + 1];

        const distance = distanceToSegment(point, p1, p2);

        if (distance < minDistance && distance <= virtualThreshold) {
          minDistance = distance;
          closestLine = line;
        }
      }
    }

    return closestLine;
  };

  const findLinesInSelectionBox = (selectionBox: { x: number; y: number; width: number; height: number }): string[] => {
    const selectedLines: string[] = [];

    const minX = Math.min(selectionBox.x, selectionBox.x + selectionBox.width);
    const maxX = Math.max(selectionBox.x, selectionBox.x + selectionBox.width);
    const minY = Math.min(selectionBox.y, selectionBox.y + selectionBox.height);
    const maxY = Math.max(selectionBox.y, selectionBox.y + selectionBox.height);

    for (const line of drawingState.lines) {
      for (const point of line.points) {
        if (point.x >= minX && point.x <= maxX && point.y >= minY && point.y <= maxY) {
          selectedLines.push(line.id);
          break;
        }
      }
    }

    return selectedLines;
  };

  const findTextNotesInSelectionBox = (selectionBox: { x: number; y: number; width: number; height: number }): string[] => {
    const selectedNotes: string[] = [];

    const minX = Math.min(selectionBox.x, selectionBox.x + selectionBox.width);
    const maxX = Math.max(selectionBox.x, selectionBox.x + selectionBox.width);
    const minY = Math.min(selectionBox.y, selectionBox.y + selectionBox.height);
    const maxY = Math.max(selectionBox.y, selectionBox.y + selectionBox.height);

    const notes = drawingState.textNotes || [];
    for (const note of notes) {
      if (
        note.x + note.width >= minX && note.x <= maxX &&
        note.y + note.height >= minY && note.y <= maxY
      ) {
        selectedNotes.push(note.id);
      }
    }

    return selectedNotes;
  };

  const findLinesIntersectingSegment = (
    start: Point,
    end: Point,
    eraserSize: number,
    scale: number
  ): Line[] => {
    const intersectingLines: Line[] = [];

    const virtualRadius = Math.max(
      MIN_ERASER_RADIUS,
      Math.min(MAX_ERASER_RADIUS, eraserSize / scale)
    );

    if (Math.abs(start.x - end.x) < 1 && Math.abs(start.y - end.y) < 1) {
      for (const line of drawingState.lines) {
        for (let i = 0; i < line.points.length - 1; i++) {
          const p1 = line.points[i];
          const p2 = line.points[i + 1];

          if (doesLineIntersectCircle(p1, p2, start, virtualRadius)) {
            intersectingLines.push(line);
            break;
          }
        }
      }
      return intersectingLines;
    }

    for (const line of drawingState.lines) {
      for (let i = 0; i < line.points.length - 1; i++) {
        const p1 = line.points[i];
        const p2 = line.points[i + 1];

        if (doLinesIntersectWithEraser(start, end, p1, p2, virtualRadius)) {
          intersectingLines.push(line);
          break;
        }
      }
    }

    return intersectingLines;
  };

  const doesLineIntersectCircle = (lineStart: Point, lineEnd: Point, circleCenter: Point, radius: number): boolean => {
    const distance = distanceToSegment(circleCenter, lineStart, lineEnd);
    return distance <= radius;
  };

  const doLinesIntersectWithEraser = (
    l1Start: Point,
    l1End: Point,
    l2Start: Point,
    l2End: Point,
    eraserRadius: number
  ): boolean => {
    const startDistance = distanceToPoint(l1Start, l2Start);
    const endDistance = distanceToPoint(l1End, l2Start);

    if (startDistance <= eraserRadius || endDistance <= eraserRadius) {
      return true;
    }

    return distanceToSegment(l2Start, l1Start, l1End) <= eraserRadius;
  };

  const distanceToPoint = (p1: Point, p2: Point): number => {
    return Math.sqrt(
      Math.pow(p1.x - p2.x, 2) +
      Math.pow(p1.y - p2.y, 2)
    );
  };

  const distanceToSegment = (p: Point, v: Point, w: Point): number => {
    const l2 = (w.x - v.x) ** 2 + (w.y - v.y) ** 2;
    if (l2 === 0) return distanceToPoint(p, v);

    let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
    t = Math.max(0, Math.min(1, t));

    return distanceToPoint(p, {
      x: v.x + t * (w.x - v.x),
      y: v.y + t * (w.y - v.y)
    });
  };

  const handlePointerDown = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isEditingText) return;

    if (e.type === 'mousedown' && (e as React.MouseEvent).button === 2) {
      startHandMode();
      setIsDragging(true);
      const pos = getPointerPosition(e);
      setLastMousePosition(pos);
      return;
    }
    
    const pos = getPointerPosition(e);
    const { offsetX, offsetY, scale, tool, selection } = uiState;
    const virtualPos = inverseTransformPoint(pos, offsetX, offsetY, scale);
    const effectiveTool = tempHandMode ? 'hand' : tool;

    if (effectiveTool === 'hand' && !tempHandMode) {
      setIsDragging(true);
      setLastMousePosition(pos);
    } else if (effectiveTool === 'pen') {
      setIsDrawing(true);
      const newLine: Line = {
        id: `line-${Date.now()}`,
        points: [virtualPos],
        color: uiState.penColor,
        width: uiState.penWidth,
        lineStyle: uiState.lineStyle,
        createdAt: Date.now()
      };
      setCurrentLine(newLine);
    } else if (effectiveTool === 'line') {
      setIsDrawing(true);
      const newLine: Line = {
        id: `line-${Date.now()}`,
        points: [virtualPos, virtualPos],
        color: uiState.penColor,
        width: uiState.penWidth,
        lineStyle: uiState.lineStyle,
        createdAt: Date.now()
      };
      setCurrentLine(newLine);
    } else if (effectiveTool === 'text') {
      const clickedNote = findTextNoteAt(virtualPos);

      if (clickedNote) {
        if (selection.includes(clickedNote.id)) {
          setIsDragging(true);
          setPrevDragPosition(virtualPos);
          setEditingTextNote(clickedNote);
        } else {
          onUiStateChange({
            ...uiState,
            selection: [clickedNote.id]
          });
        }
      } else {
        const newNote: TextNote = {
          id: `text-${Date.now()}`,
          text: '',
          x: virtualPos.x,
          y: virtualPos.y,
          fontSize: uiState.penWidth * 3,
          color: uiState.penColor,
          width: 150,
          height: 50
        };

        const newDrawingState = {
          ...drawingState,
          textNotes: [...(drawingState.textNotes || []), newNote]
        };

        onUpdateLines(newDrawingState.lines, newDrawingState.textNotes);
        onUiStateChange({
          ...uiState,
          selection: [newNote.id]
        });

        setEditingTextNote(newNote);
        setTextInputValue('');
        setTextInputHeight(50);
        setIsEditingText(true);
      }
    } else if (effectiveTool === 'eraser') {
      setIsErasing(true);
      setLastClickPosition(pos);
      setEraserStartPosition(pos);

      const now = Date.now();
      const timeDiff = now - lastClickTime;
      const posDiff = lastClickPosition
        ? Math.sqrt(
            Math.pow(pos.x - lastClickPosition.x, 2) +
            Math.pow(pos.y - lastClickPosition.y, 2)
          )
        : Infinity;

      if (timeDiff < DOUBLE_CLICK_TIME && posDiff < ERASER_CLICK_RADIUS) {
        const lineToRemove = findClosestLine(virtualPos, uiState.eraserSize, scale);
        if (lineToRemove) {
          onDeleteLine(lineToRemove.id);
          setLastErasedLineIds([lineToRemove.id]);
        }

        const noteToRemove = findTextNoteAt(virtualPos);
        if (noteToRemove) {
          const newDrawingState = {
            ...drawingState,
            textNotes: (drawingState.textNotes || []).filter(n => n.id !== noteToRemove.id)
          };
          onUpdateLines(newDrawingState.lines, newDrawingState.textNotes);
        }
      } else {
        setLastClickTime(now);

        const lineToRemove = findClosestLine(virtualPos, uiState.eraserSize, scale);
        if (lineToRemove) {
          onDeleteLine(lineToRemove.id);
          setLastErasedLineIds([lineToRemove.id]);
        }

        const noteToRemove = findTextNoteAt(virtualPos);
        if (noteToRemove) {
          const newDrawingState = {
            ...drawingState,
            textNotes: (drawingState.textNotes || []).filter(n => n.id !== noteToRemove.id)
          };
          onUpdateLines(newDrawingState.lines, newDrawingState.textNotes);
        }
      }

      setLastEraseTime(now);
    } else if (effectiveTool === 'pointer') {
      const now = Date.now();
      const timeDiff = now - lastClickTime;
      const posDiff = lastClickPosition 
        ? Math.sqrt(Math.pow(pos.x - lastClickPosition.x, 2) + Math.pow(pos.y - lastClickPosition.y, 2))
        : Infinity;

      const clickedLine = findClosestLine(virtualPos, 10, scale);
      
      if (clickedLine && timeDiff < 300 && posDiff < 20) {
        const styles: LineStyle[] = ['solid', 'dashed', 'dotted'];
        const currentIndex = styles.indexOf(clickedLine.lineStyle);
        const nextStyle = styles[(currentIndex + 1) % styles.length];
        
        const newLines = drawingState.lines.map(line =>
          line.id === clickedLine.id ? { ...line, lineStyle: nextStyle } : line
        );
        onUpdateLines(newLines, drawingState.textNotes || []);
        
        setLastClickTime(0);
      } else {
        if (selection.length > 0) {
          const selectionBox = getSelectionBoundingBox(drawingState, selection);
          if (selectionBox) {
            const handle = isPointInResizeHandle(pos, selectionBox, uiState.offsetX, uiState.offsetY, uiState.scale);
            if (handle) {
              setResizeHandle(handle);
              setResizeStart({
                box: selectionBox,
                mouse: virtualPos
              });
              setPrevResizePosition(virtualPos);
              return;
            }
          }

          for (const lineId of selection) {
            const line = drawingState.lines.find(l => l.id === lineId);
            if (line && line.points.length === 2) {
              const endpoint = isPointInLineEndpoint(pos, line, uiState.offsetX, uiState.offsetY, uiState.scale);
              if (endpoint) {
                setLineEndpointHandle(endpoint);
                setLineEndpointStart({
                  line: line,
                  mouse: virtualPos
                });
                setActiveLineForEndpoint(line);
                return;
              }
            }
          }
        }

        const clickedNote = findTextNoteAt(virtualPos);
        const isClickingSelectedLine = clickedLine && selection.includes(clickedLine.id);
        const isClickingSelectedNote = clickedNote && selection.includes(clickedNote.id);

        if (isClickingSelectedLine || isClickingSelectedNote) {
          if (!uiState.isDraggingSelection) {
            onUiStateChange({
              ...uiState,
              isDraggingSelection: true
            });
            setPrevDragPosition(virtualPos);
          }
        } else {
          onUiStateChange({
            ...uiState,
            isSelecting: true,
            selectionBox: {
              x: virtualPos.x,
              y: virtualPos.y,
              width: 0,
              height: 0
            }
          });
        }
      }

      setLastClickTime(now);
      setLastClickPosition(pos);
    }
  };

  const handlePointerMove = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (isEditingText) return;

    const pos = getPointerPosition(e);

    if (e.type === 'mousemove' && (e as React.MouseEvent).buttons === 2 && !tempHandMode) {
      startHandMode();
      setIsDragging(true);
      setLastMousePosition(pos);
    }

    const { offsetX, offsetY, scale, tool, selection, isSelecting, isDraggingSelection } = uiState;
    const effectiveTool = tempHandMode ? 'hand' : tool;

    if (effectiveTool === 'hand') {
      if (lastMousePosition) {
        const dx = pos.x - lastMousePosition.x;
        const dy = pos.y - lastMousePosition.y;

        const newOffsetX = clampOffsetX(offsetX - dx, scale, containerRef.current?.clientWidth || 0);
        const newOffsetY = clampOffsetY(offsetY - dy, scale, containerRef.current?.clientHeight || 0);

        onUiStateChange({
          ...uiState,
          offsetX: newOffsetX,
          offsetY: newOffsetY,
          scale
        });

        setLastMousePosition(pos);
      }
    } else if (effectiveTool === 'pen' && !tempHandMode) {
      if (isDrawing) {
        const virtualPos = inverseTransformPoint(pos, offsetX, offsetY, scale);

        if (currentLine) {
          const lastPoint = currentLine.points[currentLine.points.length - 1];
          const distance = Math.sqrt(
            Math.pow(virtualPos.x - lastPoint.x, 2) +
            Math.pow(virtualPos.y - lastPoint.y, 2)
          );

          if (distance > 1) {
            setCurrentLine(prev => prev ? {
              ...prev,
              points: [...prev.points, virtualPos]
            } : null);
          }
        }
      }
    } else if (effectiveTool === 'line' && !tempHandMode) {
      if (isDrawing && currentLine) {
        const virtualPos = inverseTransformPoint(pos, offsetX, offsetY, scale);
        const startPoint = currentLine.points[0];

        let adjustedPos = virtualPos;
        if (isShiftPressed && currentLine.points.length > 0) {
          const dx = virtualPos.x - startPoint.x;
          const dy = virtualPos.y - startPoint.y;
          const angle = Math.atan2(dy, dx);
          let angleDeg = angle * 180 / Math.PI;

          angleDeg = angleDeg % 360;
          if (angleDeg > 180) angleDeg -= 360;
          if (angleDeg <= -180) angleDeg += 360;

          const snapAngle = Math.round(angleDeg / 45) * 45;
          const snapAngleRad = snapAngle * Math.PI / 180;
          const distance = Math.sqrt(dx * dx + dy * dy);

          adjustedPos = {
            x: startPoint.x + distance * Math.cos(snapAngleRad),
            y: startPoint.y + distance * Math.sin(snapAngleRad)
          };
        }

        setCurrentLine({
          ...currentLine,
          points: [startPoint, adjustedPos]
        });
      }
    } else if (effectiveTool === 'eraser' && !tempHandMode) {
      if (isErasing) {
        const now = Date.now();
        const timeDiff = now - lastEraseTime;

        const scaleFactor = Math.max(0.5, Math.min(3, scale));
        const adaptiveInterval = Math.max(10, 30 - Math.floor((scaleFactor - 0.5) * 15));

        if (timeDiff > adaptiveInterval) {
          const virtualPos = inverseTransformPoint(pos, offsetX, offsetY, scale);
          const virtualLastPos = lastClickPosition ?
            inverseTransformPoint(lastClickPosition, offsetX, offsetY, scale) :
            virtualPos;

          const virtualRadius = Math.max(
            MIN_ERASER_RADIUS,
            Math.min(MAX_ERASER_RADIUS, uiState.eraserSize / scale)
          );

          const linesToRemove = findLinesIntersectingSegment(
            virtualLastPos,
            virtualPos,
            uiState.eraserSize,
            scale
          );

          const newLinesToRemove = linesToRemove.filter(
            line => !lastErasedLineIds.includes(line.id)
          );

          if (newLinesToRemove.length > 0) {
            const newErasedIds = [...lastErasedLineIds, ...newLinesToRemove.map(line => line.id)];
            const newLines = drawingState.lines.filter(
              line => !newLinesToRemove.some(removed => removed.id === line.id)
            );

            onUpdateLines(newLines, drawingState.textNotes || []);
            setLastErasedLineIds(newErasedIds);
          }

          setLastEraseTime(now);
        }

        setLastClickPosition(pos);
      }
    } else if (effectiveTool === 'pointer' && !tempHandMode) {
      const virtualPos = inverseTransformPoint(pos, offsetX, offsetY, scale);

      if (resizeHandle && resizeStart && prevResizePosition) {
        const dx = virtualPos.x - prevResizePosition.x;
        const dy = virtualPos.y - prevResizePosition.y;

        const { box } = resizeStart;
        let newBox = { ...box };

        switch (resizeHandle) {
          case 'nw':
            newBox = {
              x: box.x + dx,
              y: box.y + dy,
              width: box.width - dx,
              height: box.height - dy
            };
            break;
          case 'n':
            newBox = {
              x: box.x,
              y: box.y + dy,
              width: box.width,
              height: box.height - dy
            };
            break;
          case 'ne':
            newBox = {
              x: box.x,
              y: box.y + dy,
              width: box.width + dx,
              height: box.height - dy
            };
            break;
          case 'e':
            newBox = {
              x: box.x,
              y: box.y,
              width: box.width + dx,
              height: box.height
            };
            break;
          case 'se':
            newBox = {
              x: box.x,
              y: box.y,
              width: box.width + dx,
              height: box.height + dy
            };
            break;
          case 's':
            newBox = {
              x: box.x,
              y: box.y,
              width: box.width,
              height: box.height + dy
            };
            break;
          case 'sw':
            newBox = {
              x: box.x + dx,
              y: box.y,
              width: box.width - dx,
              height: box.height + dy
            };
            break;
          case 'w':
            newBox = {
              x: box.x + dx,
              y: box.y,
              width: box.width - dx,
              height: box.height
            };
            break;
        }

        if (newBox.width < MIN_SELECTION_SIZE) {
          if (resizeHandle === 'nw' || resizeHandle === 'sw' || resizeHandle === 'w') {
            newBox.x = box.x + box.width - MIN_SELECTION_SIZE;
          }
          newBox.width = MIN_SELECTION_SIZE;
        }

        if (newBox.height < MIN_SELECTION_SIZE) {
          if (resizeHandle === 'nw' || resizeHandle === 'ne' || resizeHandle === 'n') {
            newBox.y = box.y + box.height - MIN_SELECTION_SIZE;
          }
          newBox.height = MIN_SELECTION_SIZE;
        }

        if (isShiftPressed) {
          const aspectRatio = box.width / box.height;
          if (resizeHandle === 'nw' || resizeHandle === 'se') {
            newBox.height = newBox.width / aspectRatio;
          } else if (resizeHandle === 'ne' || resizeHandle === 'sw') {
            newBox.height = newBox.width / aspectRatio;
          } else if (resizeHandle === 'n' || resizeHandle === 's') {
            newBox.width = newBox.height * aspectRatio;
          } else if (resizeHandle === 'e' || resizeHandle === 'w') {
            newBox.height = newBox.width / aspectRatio;
          }
        }

        const newLines = drawingState.lines.map(line => {
          if (selection.includes(line.id)) {
            return {
              ...line,
              points: line.points.map(point => {
                const relativeX = (point.x - box.x) / box.width;
                const relativeY = (point.y - box.y) / box.height;

                return {
                  x: newBox.x + relativeX * newBox.width,
                  y: newBox.y + relativeY * newBox.height
                };
              })
            };
          }
          return line;
        });

        const newTextNotes = (drawingState.textNotes || []).map(note => {
          if (selection.includes(note.id)) {
            const relativeX = (note.x - box.x) / box.width;
            const relativeY = (note.y - box.y) / box.height;
            const relativeWidth = note.width / box.width;
            const relativeHeight = note.height / box.height;

            return {
              ...note,
              x: newBox.x + relativeX * newBox.width,
              y: newBox.y + relativeY * newBox.height,
              width: relativeWidth * newBox.width,
              height: relativeHeight * newBox.height
            };
          }
          return note;
        });

        onUpdateLines(newLines, newTextNotes);

        setResizeStart({
          box: newBox,
          mouse: virtualPos
        });

        setPrevResizePosition(virtualPos);
      } else if (lineEndpointHandle && lineEndpointStart && activeLineForEndpoint) {
        const { line, mouse: startMouse } = lineEndpointStart;

        const dx = virtualPos.x - startMouse.x;
        const dy = virtualPos.y - startMouse.y;

        let newPoints = [...line.points];

        if (lineEndpointHandle === 'start') {
          newPoints[0] = {
            x: line.points[0].x + dx,
            y: line.points[0].y + dy
          };
        } else {
          newPoints[1] = {
            x: line.points[1].x + dx,
            y: line.points[1].y + dy
          };
        }

        const newLines = drawingState.lines.map(l =>
          l.id === line.id ? { ...l, points: newPoints } : l
        );

        onUpdateLines(newLines, drawingState.textNotes || []);

        setLineEndpointStart({
          line: { ...line, points: newPoints },
          mouse: virtualPos
        });
      } else if (isSelecting && uiState.selectionBox) {
        const startX = uiState.selectionBox.x;
        const startY = uiState.selectionBox.y;
        const width = virtualPos.x - startX;
        const height = virtualPos.y - startY;

        onUiStateChange({
          ...uiState,
          selectionBox: {
            x: Math.min(startX, virtualPos.x),
            y: Math.min(startY, virtualPos.y),
            width: Math.abs(width),
            height: Math.abs(height)
          }
        });
      } else if (isDraggingSelection && prevDragPosition && selection.length > 0) {
        const dx = virtualPos.x - prevDragPosition.x;
        const dy = virtualPos.y - prevDragPosition.y;

        const newLines = drawingState.lines.map(line => {
          if (selection.includes(line.id)) {
            return {
              ...line,
              points: line.points.map(point => ({
                x: point.x + dx,
                y: point.y + dy
              }))
            };
          }
          return line;
        });

        const newTextNotes = (drawingState.textNotes || []).map(note => {
          if (selection.includes(note.id)) {
            return {
              ...note,
              x: note.x + dx,
              y: note.y + dy
            };
          }
          return note;
        });

        onUpdateLines(newLines, newTextNotes);

        setPrevDragPosition(virtualPos);
      }
    }
  };

  const handlePointerUp = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'mouseup' && (e as React.MouseEvent).button === 2) {
      setIsDragging(false);
      setLastMousePosition(null);
      endHandMode();
      return;
    }

    setIsDragging(false);
    setLastMousePosition(null);

    setResizeHandle(null);
    setResizeStart(null);
    setPrevDragPosition(null);
    setPrevResizePosition(null);

    setLineEndpointHandle(null);
    setLineEndpointStart(null);
    setActiveLineForEndpoint(null);

    if (uiState.tool === 'pen' && isDrawing && currentLine && currentLine.points.length > 1) {
      const newDrawingState = {
        ...drawingState,
        lines: [...drawingState.lines, currentLine]
      };
      onUpdateLines(newDrawingState.lines, newDrawingState.textNotes || []);
    } else if (uiState.tool === 'line' && isDrawing && currentLine && currentLine.points.length === 2) {
      const startPoint = currentLine.points[0];
      const endPoint = currentLine.points[1];
      const distance = Math.sqrt(
        Math.pow(endPoint.x - startPoint.x, 2) +
        Math.pow(endPoint.y - startPoint.y, 2)
      );

      if (distance > MIN_LINE_LENGTH) {
        const newDrawingState = {
          ...drawingState,
          lines: [...drawingState.lines, currentLine]
        };
        onUpdateLines(newDrawingState.lines, newDrawingState.textNotes || []);
      }
    }

    setIsDrawing(false);
    setCurrentLine(null);

    if (uiState.tool === 'eraser' && isErasing) {
      setIsErasing(false);
      //setLastErasedLineIds([]);
      setEraserStartPosition(null);
    }

    if (uiState.tool === 'pointer') {
      if (uiState.isSelecting && uiState.selectionBox) {
        const { selectionBox } = uiState;

        if (Math.abs(selectionBox.width) > SELECTION_THRESHOLD ||
            Math.abs(selectionBox.height) > SELECTION_THRESHOLD) {
          const newSelection = findLinesInSelectionBox(selectionBox);
          const newTextSelection = findTextNotesInSelectionBox(selectionBox);

          onUiStateChange({
            ...uiState,
            selection: [...newSelection, ...newTextSelection],
            isSelecting: false,
            selectionBox: undefined
          });
        } else {
          const pos = getPointerPosition(e);
          const { offsetX, offsetY, scale } = uiState;
          const virtualPos = inverseTransformPoint(pos, offsetX, offsetY, scale);

          const clickedLine = findClosestLine(virtualPos, 10, scale);
          const clickedNote = findTextNoteAt(virtualPos);

          if (clickedLine) {
            const isSelected = uiState.selection.includes(clickedLine.id);
            const newSelection = isSelected
              ? uiState.selection.filter(id => id !== clickedLine!.id)
              : [...uiState.selection, clickedLine.id];

            onUiStateChange({
              ...uiState,
              selection: newSelection,
              isSelecting: false,
              selectionBox: undefined
            });
          } else if (clickedNote) {
            const isSelected = uiState.selection.includes(clickedNote.id);
            const newSelection = isSelected
              ? uiState.selection.filter(id => id !== clickedNote!.id)
              : [...uiState.selection, clickedNote.id];

            onUiStateChange({
              ...uiState,
              selection: newSelection,
              isSelecting: false,
              selectionBox: undefined
            });
          } else {
            onUiStateChange({
              ...uiState,
              selection: [],
              isSelecting: false,
              selectionBox: undefined
            });
          }
        }
      }

      if (uiState.isDraggingSelection) {
        onUiStateChange({
          ...uiState,
          isDraggingSelection: false
        });
      }
    }
  };

  const handlePointerLeave = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);
    setLastMousePosition(null);
    setIsDrawing(false);
    setCurrentLine(null);

    if (uiState.tool === 'eraser' && isErasing) {
      setIsErasing(false);
      setLastErasedLineIds([]);
      setEraserStartPosition(null);
    }

    if (uiState.tool === 'pointer' && uiState.isSelecting) {
      onUiStateChange({
        ...uiState,
        isSelecting: false,
        selectionBox: undefined
      });
    }
  };

  const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const { offsetX, offsetY, scale } = uiState;
    const pos = getPointerPosition(e);
    const container = containerRef.current;
    const width = container ? container.clientWidth : 0;
    const height = container ? container.clientHeight : 0;

    const virtualPos = inverseTransformPoint(pos, offsetX, offsetY, scale);

    let newScale = scale * (e.deltaY > 0 ? 0.9 : 1.1);
    newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));

    const newOffsetX = virtualPos.x * newScale - pos.x;
    const newOffsetY = virtualPos.y * newScale - pos.y;

    const clampedOffsetX = clampOffsetX(newOffsetX, newScale, width);
    const clampedOffsetY = clampOffsetY(newOffsetY, newScale, height);

    onUiStateChange({
      ...uiState,
      offsetX: clampedOffsetX,
      offsetY: clampedOffsetY,
      scale: newScale
    });
  };

  const getCursorClass = () => {
    if (tempHandMode || (uiState.tool === 'hand' && isDragging)) {
      return 'cursor-grabbing';
    } else if (tempHandMode || uiState.tool === 'hand') {
      return 'cursor-grab';
    } else if (uiState.tool === 'eraser') {
      return 'cursor-eraser';
    } else if (uiState.tool === 'pen') {
      return 'cursor-pen';
    } else if (uiState.tool === 'text') {
      return 'cursor-text';
    } else if (uiState.tool === 'pointer') {
      if (resizeHandle) {
        switch (resizeHandle) {
          case 'nw':
          case 'se':
            return 'cursor-nwse-resize';
          case 'ne':
          case 'sw':
            return 'cursor-nesw-resize';
          case 'n':
          case 's':
            return 'cursor-ns-resize';
          case 'e':
          case 'w':
            return 'cursor-ew-resize';
        }
      }
      return 'cursor-pointer';
    } else if (uiState.tool === 'line') {
      return 'cursor-line';
    }
    return '';
  };

  return (
    <>
      <div
        ref={containerRef}
        className={`canvas-container ${getCursorClass()}`}
        onMouseDown={handlePointerDown}
        onMouseMove={handlePointerMove}
        onMouseUp={handlePointerUp}
        onMouseLeave={handlePointerLeave}
        onTouchStart={handlePointerDown}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerUp}
        onTouchCancel={handlePointerLeave}
        onWheel={handleWheel}
        onContextMenu={handleContextMenu}
        tabIndex={0}
        style={{ outline: 'none', position: 'relative', touchAction: 'none' }}
      >
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height: '100%' }}
        />
        {isEditingText && editingTextNote && (
          <div
            className="text-editor-overlay"
            style={{
              position: 'absolute',
              left: editingTextNote.x * uiState.scale - uiState.offsetX,
              top: editingTextNote.y * uiState.scale - uiState.offsetY,
              width: editingTextNote.width * uiState.scale,
              height: textInputHeight * uiState.scale,
              zIndex: 1000
            }}
          >
            <textarea
              ref={textInputRef}
              value={textInputValue}
              onChange={(e) => {
                setTextInputValue(e.target.value);
                const newHeight = calculateTextHeight(
                  e.target.value,
                  editingTextNote.fontSize,
                  editingTextNote.width
                );
                setTextInputHeight(newHeight);
              }}
              onBlur={(e) => {
                const newText = e.target.value;
                const newHeight = calculateTextHeight(
                  newText,
                  editingTextNote.fontSize,
                  editingTextNote.width
                );

                const newNote = {
                  ...editingTextNote,
                  text: newText,
                  height: newHeight
                };

                const newTextNotes = (drawingState.textNotes || []).map(n =>
                  n.id === newNote.id ? newNote : n
                );

                onUpdateLines(drawingState.lines, newTextNotes);
                setIsEditingText(false);
                setEditingTextNote(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.ctrlKey) {
                  e.preventDefault();
                  const newText = e.currentTarget.value;
                  const newHeight = calculateTextHeight(
                    newText,
                    editingTextNote.fontSize,
                    editingTextNote.width
                  );

                  const newNote = {
                    ...editingTextNote,
                    text: newText,
                    height: newHeight
                  };

                  const newTextNotes = (drawingState.textNotes || []).map(n =>
                    n.id === newNote.id ? newNote : n
                  );

                  onUpdateLines(drawingState.lines, newTextNotes);
                  setIsEditingText(false);
                  setEditingTextNote(null);
                } else if (e.key === 'Escape') {
                  setIsEditingText(false);
                  setEditingTextNote(null);
                }
              }}
              style={{
                width: '100%',
                height: '100%',
                fontSize: editingTextNote.fontSize * uiState.scale,
                color: editingTextNote.color,
                background: 'transparent',
                border: '2px solid #3498db',
                borderRadius: '4px',
                resize: 'none',
                padding: '5px',
                outline: 'none',
                overflow: 'hidden',
                lineHeight: '1.2'
              }}
              autoFocus
              placeholder="Введите текст..."
            />
          </div>
        )}
      </div>
    </>
  );
};